import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The shared page width for every homepage section, the hero and the footer:
 * 1440px with generous gutters, so content runs edge to edge of the
 * container instead of leaving a decorative column on the left.
 */
export const PAGE_CONTAINER = 'relative mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-12'

/** Section eyebrow: a pill with a lime pixel, used above every section title. */
export function SectionTag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full bg-secondary/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.16em] text-foreground ring-1 ring-foreground/10',
        className,
      )}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-xs bg-primary shadow-[0_0_8px] shadow-primary/60" />
      {children}
    </span>
  )
}

/**
 * Retired: the old left-hand coordinate rail. No page renders it any more;
 * kept exported only so nothing importing it breaks.
 */
export function MeridianRail({ degree, className }: { degree?: number; className?: string }) {
  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-y-0 left-4 hidden w-40 sm:left-8 lg:block', className)}>
      <div className="absolute inset-y-0 left-1/2 w-px bg-foreground/10" />
      {degree !== undefined && (
        <>
          <div className="absolute left-1/2 top-0 h-px w-3 -translate-x-1/2 bg-foreground/30" />
          <span className="absolute left-[calc(50%_+_14px)] top-0 -translate-y-1/2 font-mono text-[11px] tabular-nums text-muted-foreground">
            +{String(degree).padStart(2, '0')}°
          </span>
        </>
      )}
    </div>
  )
}

/** Retired with the rail: a coordinate label. No page renders it any more. */
export function CoordinateLabel({ coord, children }: { coord: string; children?: ReactNode }) {
  return (
    <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1">
      <span aria-hidden className="absolute -left-20 top-1/2 hidden h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground lg:block" />
      <span aria-hidden className="absolute -left-[4.5rem] top-1/2 hidden h-px w-16 bg-foreground/10 lg:block" />
      <span className="font-mono text-sm uppercase tracking-[0.16em] text-muted-foreground">{coord}</span>
      {children}
    </div>
  )
}

export function MeridianSection({
  id,
  tag,
  title,
  lede,
  actions,
  children,
  className,
  backdrop,
}: {
  id?: string
  tag?: string
  title: ReactNode
  lede?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  backdrop?: ReactNode
}) {
  return (
    <section id={id} aria-labelledby={id ? `${id}-title` : undefined} className={cn('relative isolate', className)}>
      {backdrop}
      <div className={cn(PAGE_CONTAINER, 'pt-24')}>
        <header className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            {tag && <SectionTag>{tag}</SectionTag>}
            <h2
              id={id ? `${id}-title` : undefined}
              className="mt-4 font-heading text-4xl font-medium leading-[1.02] tracking-tight text-foreground md:text-5xl lg:text-6xl"
            >
              {title}
            </h2>
            {lede && <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground/80 md:text-lg">{lede}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">{actions}</div>}
        </header>
        <div className="mt-10">{children}</div>
      </div>
    </section>
  )
}

/** A constellation-line data row: label, dotted 1px leader ending in a 4px node, value. */
export function StarRow({
  label,
  value,
  emphasis = false,
  className,
}: {
  label: ReactNode
  value: ReactNode
  emphasis?: boolean
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-3 py-2', className)}>
      <dt className={cn('shrink-0 text-sm', emphasis ? 'font-semibold text-foreground' : 'text-foreground/80')}>{label}</dt>
      <span aria-hidden className="leader-dots h-0.5 min-w-6 flex-1" />
      <span aria-hidden className={cn('h-1 w-1 shrink-0 rounded-full', emphasis ? 'bg-foreground' : 'bg-muted-foreground')} />
      <dd
        className={cn(
          'min-w-0 text-right font-mono tabular-nums text-foreground',
          emphasis ? 'text-lg font-semibold md:text-xl' : 'text-sm',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
